/* Wishlist — integrates with https://wishlistsdk.whisperdemo.online/api */

;(function () {
  const API_BASE = 'https://wishlistsdk.whisperdemo.online/api'
  const SHOP_ID = 1

  // One-time storage reset — bump this version to clear all cached wishlist data
  const WL_STORAGE_VERSION = '2'
  if (localStorage.getItem('wl_v') !== WL_STORAGE_VERSION) {
    localStorage.removeItem('wl_item_map')
    localStorage.removeItem('wl_guest_id')
    localStorage.setItem('wl_v', WL_STORAGE_VERSION)
  }

  function getOrCreateGuestId() {
    let stored = localStorage.getItem('wl_guest_id')
    let numId = Number(stored)
    if (!stored || isNaN(numId) || numId <= 0) {
      numId = Date.now() * 1000 + Math.floor(Math.random() * 1000)
      localStorage.setItem('wl_guest_id', numId)
    }
    return numId
  }

  const CUSTOMER_ID =
    (window.theme && window.theme.wishlist && window.theme.wishlist.customer_id) ||
    getOrCreateGuestId()

  // -- Storage helpers --
  function getItemMap() {
    try { return JSON.parse(localStorage.getItem('wl_item_map') || '{}') } catch (_) { return {} }
  }
  function setItemMap(map) {
    localStorage.setItem('wl_item_map', JSON.stringify(map))
  }

  // -- API calls --
  async function fetchAllLists() {
    const params = new URLSearchParams()
    params.set('shop_id', SHOP_ID)
    params.set('customer_id', CUSTOMER_ID)
    const res = await fetch(`${API_BASE}/lists?${params}`)
    if (!res.ok) return []
    const data = await res.json()
    return Array.isArray(data) ? data : data.lists || data.data || []
  }

  async function fetchListItems(listId) {
    const params = new URLSearchParams()
    params.set('list_id', listId)
    const res = await fetch(`${API_BASE}/list/items?${params}`)
    if (!res.ok) return []
    const data = await res.json()
    return Array.isArray(data) ? data : data.items || data.data || []
  }

  async function addItem(listId, token, product) {
    const res = await fetch(`${API_BASE}/list/items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        list_id: listId,
        token: token,
        external_id: product.id,
        external_type: 'ProductVariant',
        handle: product.handle,
        name: product.title,
        image_url: product.image,
        price: product.price,
        sku: product.sku || '',
        color: product.color || '',
        size: product.size || '',
        product_card_tag: 'none',
      }),
    })
    const data = await res.json()
    return data.id || (data.item && data.item.id) || null
  }

  async function deleteItem(itemId, token) {
    await fetch(`${API_BASE}/list/items/${itemId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: token || undefined }),
    })
  }

  async function deleteList(listId, token) {
    await fetch(`${API_BASE}/lists/${listId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
  }

  // -- State sync: fetch ALL lists + ALL items, build complete map --
  async function syncState() {
    try {
      const lists = await fetchAllLists()
      const map = {}
      await Promise.all(
        lists.map(async (list) => {
          const items = await fetchListItems(list.id)
          items.forEach((item) => {
            const pid = String(item.product_id || item.productId || item.external_id || '')
            if (pid && item.id) {
              map[pid] = { id: item.id, token: list.token || item.token || null }
            }
          })
        })
      )
      setItemMap(map)
      applyHeartStates()
    } catch (_) {}
  }

  function applyHeartStates() {
    const map = getItemMap()
    document.querySelectorAll('.wishlist-btn').forEach((btn) => {
      const pid = btn.dataset.productId
      if (!pid) return
      const wishlisted = !!map[pid]
      btn.classList.toggle('is-wishlisted', wishlisted)
      btn.setAttribute('aria-label', wishlisted ? 'Remove from wishlist' : 'Add to wishlist')
    })
  }

  // -- Toggle handler --
  async function toggleWishlist(btn) {
    if (btn.classList.contains('wishlist-loading')) return

    const pid = btn.dataset.productId
    const map = getItemMap()
    const isWishlisted = map[pid] || btn.classList.contains('is-wishlisted')

    if (isWishlisted) {
      const entry = map[pid]
      const itemId = entry ? (entry.id || entry) : null
      const itemToken = entry ? (entry.token || null) : null
      if (!itemId) return

      btn.classList.add('wishlist-loading')
      try {
        await deleteItem(itemId, itemToken)
        delete map[pid]
        setItemMap(map)
        btn.classList.remove('is-wishlisted')
        btn.setAttribute('aria-label', 'Add to wishlist')

        const wishlistItem = btn.closest('.wishlist-item')
        if (wishlistItem) {
          wishlistItem.remove()
          const container = document.getElementById('wishlist-page-items')
          if (container && !container.querySelector('.wishlist-item')) {
            container.innerHTML = `<p class="wishlist-category-empty">There are no wishlist items in this category.</p>`
          }
        }
        showToast('Item removed from wishlist.')
        refreshListCards()
      } catch (err) {
        console.error('[Wishlist]', err)
        showToast('Failed to remove item.', 'error')
      } finally {
        btn.classList.remove('wishlist-loading')
      }
    } else {
      showListSelectModal(btn)
    }
  }

  // -- Toast --
  function showToast(message, type = 'success') {
    const toast = document.createElement('div')
    toast.className = `wl-toast wl-toast--${type}`
    toast.textContent = message
    document.body.appendChild(toast)
    requestAnimationFrame(() => requestAnimationFrame(() => toast.classList.add('wl-toast--visible')))
    setTimeout(() => {
      toast.classList.remove('wl-toast--visible')
      setTimeout(() => toast.remove(), 300)
    }, 3000)
  }

  // -- List-select modal --
  let _modalEl = null

  function getOrCreateModal() {
    if (_modalEl) return _modalEl
    _modalEl = document.createElement('div')
    _modalEl.id = 'wl-list-modal-overlay'
    _modalEl.innerHTML = `
      <div class="wl-list-modal" role="dialog" aria-modal="true">
        <div class="wl-list-modal__header">
          <h3 class="wl-list-modal__title">Add to Wishlist</h3>
          <button class="wl-list-modal__close" type="button" aria-label="Close">&times;</button>
        </div>
        <div class="wl-list-modal__body" id="wl-list-modal-body"></div>
        <div class="wl-list-modal__footer">
          <button class="wl-list-modal__submit button accent" type="button">Add</button>
        </div>
      </div>
    `
    _modalEl.querySelector('.wl-list-modal__close').addEventListener('click', hideListSelectModal)
    _modalEl.addEventListener('click', (e) => { if (e.target === _modalEl) hideListSelectModal() })
    document.body.appendChild(_modalEl)
    return _modalEl
  }

  function hideListSelectModal() {
    if (_modalEl) _modalEl.hidden = true
  }

  async function showListSelectModal(btn) {
    const modal = getOrCreateModal()
    const body = modal.querySelector('#wl-list-modal-body')
    const submitBtn = modal.querySelector('.wl-list-modal__submit')

    body.innerHTML = '<p class="wl-list-modal__loading">Loading…</p>'
    modal.hidden = false

    let lists = []
    try { lists = await fetchAllLists() } catch (_) {}

    if (!lists.length) {
      body.innerHTML = '<p class="wl-list-modal__empty">No wishlists found. <a href="/pages/wishlist" style="text-decoration:underline;color:inherit;">Create one first</a>.</p>'
      return
    }

    body.innerHTML = lists.map((list, i) => `
      <label class="wl-list-modal__option">
        <input type="radio" name="wl-list" value="${list.id}" data-token="${list.token || ''}" ${i === 0 ? 'checked' : ''}>
        <span class="wl-list-modal__option-name">${list.name || 'Untitled'}</span>
        <span class="wl-list-modal__option-count">${Array.isArray(list.items) ? list.items.length : 0} items</span>
      </label>
    `).join('')

    const oldSubmit = submitBtn.cloneNode(true)
    submitBtn.parentNode.replaceChild(oldSubmit, submitBtn)

    oldSubmit.addEventListener('click', async () => {
      const selected = modal.querySelector('input[name="wl-list"]:checked')
      if (!selected) return

      const listId = selected.value
      const token = selected.dataset.token

      oldSubmit.disabled = true
      oldSubmit.textContent = 'Adding…'

      try {
        const product = {
          id: btn.dataset.productId,
          handle: btn.dataset.productHandle,
          title: btn.dataset.productTitle,
          url: btn.dataset.productUrl,
          image: btn.dataset.productImage,
          price: btn.dataset.productPrice,
          sku: btn.dataset.sku,
          color: btn.dataset.color,
          size: btn.dataset.size,
        }
        const itemId = await addItem(listId, token, product)
        if (itemId) {
          const map = getItemMap()
          map[btn.dataset.productId] = { id: itemId, token: token }
          setItemMap(map)
          await syncState()
        }
        btn.classList.add('is-wishlisted')
        btn.setAttribute('aria-label', 'Remove from wishlist')
        hideListSelectModal()
        showToast('Item added to wishlist.')
      } catch (err) {
        console.error('[Wishlist]', err)
        showToast('Failed to add item. Please try again.', 'error')
      } finally {
        oldSubmit.disabled = false
        oldSubmit.textContent = 'Add'
      }
    })
  }

  // -- Event delegation --
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.wishlist-btn')
    if (!btn) return
    e.preventDefault()
    e.stopImmediatePropagation()
    toggleWishlist(btn)
  }, true)

  // -- Wishlist page --
  function formatMoney(cents) {
    const amount = (parseInt(cents, 10) / 100).toFixed(2)
    return `$${amount}`
  }

  function renderListItems(items, listToken) {
    const container = document.getElementById('wishlist-page-items')
    if (!container) return

    const map = getItemMap()
    items.forEach((item) => {
      const pid = String(item.product_id || item.productId || item.external_id || '')
      if (pid && item.id) map[pid] = { id: item.id, token: listToken || item.token || null }
    })
    setItemMap(map)
    container.innerHTML = ''

    if (!items.length) {
      container.innerHTML = `<p class="wishlist-category-empty">There are no wishlist items in this category.</p>`
      return
    }

    const ul = document.createElement('ul')
    ul.className = 'wishlist-page__grid'

    items.forEach((item) => {
      const pid = String(item.product_id || item.productId || item.external_id || '')
      const iid = item.id
      const title = item.product_title || item.name || item.title || ''
      const url = item.product_url || item.url || '#'
      const image = item.product_image || item.image_url || item.image || ''
      const price = item.product_price || item.price || ''

      const li = document.createElement('li')
      li.className = 'wishlist-item'
      li.dataset.itemId = iid
      li.innerHTML = `
        <div class="wishlist-item__image-wrap">
          ${image ? `<img src="${image}" alt="${title}" loading="lazy">` : ''}
          <div class="wishlist-item__actions">
            <button class="wishlist-btn is-wishlisted"
              data-product-id="${pid}"
              data-product-handle="${item.handle || ''}"
              data-product-title="${title}"
              data-product-url="${url}"
              data-product-image="${image}"
              data-product-price="${price}"
              data-sku="${item.sku || ''}"
              data-color="${item.color || ''}"
              data-size="${item.size || ''}"
              aria-label="Remove from wishlist"
              type="button">
              <svg class="heart-outline" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 21C12 21 3 14.5 3 8.5C3 5.42 5.42 3 8.5 3C10.24 3 11.91 3.81 13 5.08C14.09 3.81 15.76 3 17.5 3C20.58 3 23 5.42 23 8.5C23 14.5 14 21 12 21Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <svg class="heart-filled" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 21C12 21 3 14.5 3 8.5C3 5.42 5.42 3 8.5 3C10.24 3 11.91 3.81 13 5.08C14.09 3.81 15.76 3 17.5 3C20.58 3 23 5.42 23 8.5C23 14.5 14 21 12 21Z" fill="#c00" stroke="#c00" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
        <div class="wishlist-item__title"><a href="${url}">${title}</a></div>
        ${price ? `<div class="wishlist-item__price">${formatMoney(price)}</div>` : ''}
      `
      ul.appendChild(li)
    })

    container.appendChild(ul)
  }

  async function refreshListCards() {
    const grid = document.getElementById('wishlist-projects-grid')
    if (!grid) return
    try {
      const lists = await fetchAllLists()
      lists.forEach((list) => {
        const card = grid.querySelector(`[data-list-id="${list.id}"]`)
        if (!card) return
        const countEl = card.querySelector('.wishlist-project-card__count')
        if (countEl) {
          const count = Array.isArray(list.items) ? list.items.length : (list.items || 0)
          countEl.textContent = `${count} items`
        }
      })
    } catch (_) {}
  }

  async function renderProjectsPage() {
    const grid = document.getElementById('wishlist-projects-grid')
    if (!grid) return

    try {
      const lists = await fetchAllLists()
      grid.querySelectorAll('.wishlist-project-card').forEach((el) => el.remove())
      lists.forEach((list) => addProjectCard(grid, list))
    } catch (_) {}

    const createBtn = document.getElementById('wishlist-create-btn')
    const overlay = document.getElementById('wishlist-modal-overlay')
    const closeBtn = document.getElementById('wishlist-modal-close')
    const submitBtn = document.getElementById('wishlist-modal-submit')
    const nameInput = document.getElementById('wishlist-project-name')
    const errorMsg = document.getElementById('wishlist-modal-error')

    if (!createBtn) return

    createBtn.addEventListener('click', () => {
      nameInput.value = ''
      errorMsg.hidden = true
      overlay.hidden = false
      nameInput.focus()
    })
    closeBtn.addEventListener('click', () => { overlay.hidden = true })
    overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.hidden = true })
    nameInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') submitBtn.click() })

    submitBtn.addEventListener('click', async () => {
      const name = nameInput.value.trim()
      if (!name) { errorMsg.hidden = false; nameInput.focus(); return }
      errorMsg.hidden = true
      submitBtn.disabled = true
      submitBtn.textContent = 'Creating…'

      try {
        const res = await fetch(`${API_BASE}/lists`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ shop_id: SHOP_ID, customer_id: CUSTOMER_ID, name }),
        })
        const data = await res.json()
        const list = data.list || data
        addProjectCard(grid, list)
        overlay.hidden = true
        showToast('Wishlist created.')
        await syncState()
      } catch (err) {
        console.error('[Wishlist]', err)
        showToast('Failed to create wishlist. Please try again.', 'error')
      } finally {
        submitBtn.disabled = false
        submitBtn.textContent = 'Create'
      }
    })
  }

  function addProjectCard(grid, list) {
    const card = document.createElement('div')
    card.className = 'wishlist-project-card'
    card.dataset.listId = list.id
    card.innerHTML = `
      <div class="wishlist-project-card__body">
        <div class="wishlist-project-card__name">${list.name || 'Untitled'}</div>
        <div class="wishlist-project-card__count">${Array.isArray(list.items) ? list.items.length : 0} items</div>
      </div>
      <button class="wishlist-project-card__delete" data-list-id="${list.id}" aria-label="Delete list" type="button">
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
        </svg>
      </button>
    `

    card.addEventListener('click', (e) => {
      if (e.target.closest('.wishlist-project-card__delete')) return
      grid.querySelectorAll('.wishlist-project-card').forEach((c) => c.classList.remove('is-selected'))
      card.classList.add('is-selected')
      if (list.items && list.items.length > 0) {
        renderListItems(list.items, list.token)
      } else {
        const container = document.getElementById('wishlist-page-items')
        if (container) container.innerHTML = `<p class="wishlist-category-empty">There are no wishlist items in this category.</p>`
      }
    })

    card.querySelector('.wishlist-project-card__delete').addEventListener('click', async (e) => {
      e.stopPropagation()
      try {
        await deleteList(list.id, list.token)
        if (card.classList.contains('is-selected')) {
          const container = document.getElementById('wishlist-page-items')
          if (container) container.innerHTML = ''
        }
        card.remove()
        showToast('Wishlist deleted.')
      } catch (err) {
        console.error('[Wishlist]', err)
        showToast('Failed to delete wishlist. Please try again.', 'error')
      }
    })

    grid.appendChild(card)
  }

  // -- Init --
  document.addEventListener('DOMContentLoaded', () => {
    syncState()
    renderProjectsPage()
  })
})()
